import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix";
import {
	type LinksFunction,
	type LoaderFunctionArgs,
	json,
} from "@remix-run/node"
import {
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	isRouteErrorResponse,
	useLoaderData,
	useRouteError,
} from "@remix-run/react"
import { useEffect } from "react"
import { getToast } from "remix-toast"
import { Toaster, toast as notify } from "sonner"
import styles from "./tailwind.css?url"
import { abstract } from "viem/chains"
import { AbstractWalletProvider } from "@abstract-foundation/agw-react"

export const loader = async ({ request }: LoaderFunctionArgs) => {
	const { toast, headers } = await getToast(request)
	return json({ toast }, { headers })
}

export const links: LinksFunction = () => [{ rel: "stylesheet", href: styles }]

export function ErrorBoundary() {
    const error = useRouteError()

    captureRemixErrorBoundaryError(error);

    if (isRouteErrorResponse(error)) {
		const formattedErrorData = error.data.replace(/\n/g, "<br />")

		return (
			<div className="flex flex-col justify-center items-center m-w-[100dvw] h-[100dvh] p-[max(10dvw,1rem)] text-center">
				<h1>
					{error.status} {error.statusText}
				</h1>
				<p
					className="mt-4 text-center w-4/5"
					dangerouslySetInnerHTML={{ __html: formattedErrorData }}
				/>
			</div>
		)
	} else if (error instanceof Error) {
		return (
			<div className="flex flex-col justify-center items-center mt-8">
				<h1>Error</h1>
				<p>{error.message}</p>
				<p>The stack trace is:</p>
				<pre>{error.stack}</pre>
			</div>
		)
	} else {
		return <h1>Unknown Error</h1>
	}
}

export function Layout({ children }: { children: React.ReactNode }) {
	return (
		<html lang="en">
			<head>
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width, initial-scale=1" />
				<Meta />
				<Links />
			</head>
			<body className="text-foreground font-sans font-semibold text-sm antialiased" suppressHydrationWarning>
				{/* Fixed background element that works on mobile */}
				<div 
					className="fixed inset-0 -z-10"
					style={{
						backgroundColor: "rgba(255, 255, 255, 0.2)",
						backgroundImage: "url('/images/custom-bg.png')", 
						backgroundSize: "cover", 
						backgroundRepeat: "no-repeat", 
						backgroundPosition: "center"
					}}
				/>
				<div className="relative mx-auto grid h-full min-h-dvh w-full max-w-md grid-rows-[auto_1fr] border-foreground/10 border-x">
					{children}
				</div>
				<ScrollRestoration />
				<Scripts />
				<Toaster position="top-center" />
			</body>
		</html>
	)
}

function App() {
	const { toast } = useLoaderData<typeof loader>()

	useEffect(() => {
		if (toast?.type === "error") {
			notify.error(toast.message)
		}
		if (toast?.type === "success") {
			notify.success(toast.message)
		}
	}, [toast])
	return <AbstractWalletProvider chain={abstract}><Outlet /></AbstractWalletProvider>
}

export default withSentry(App);
